import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ImageWithFallback } from './ImageWithFallback'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface LightboxProps {
  images: string[]
  index: number
  onIndexChange: (i: number) => void
  onClose: () => void
  /**
   * Map a raw entry in `images` to a displayable URL. Defaults to identity, so
   * callers that already hold full URLs can omit it; callers holding storage
   * keys pass their key→URL builder (e.g. the parcels list).
   */
  toUrl?: (raw: string) => string
}

/**
 * Full-screen image viewer rendered into `document.body` via a portal so it
 * escapes any positioned/overflow-clipped ancestor (drawers, cards). Escape /
 * click-anywhere closes; ←/→ page through multiple images. Body scroll is
 * locked while open. Sits at the top of the z-index ladder (`z-[100]`,
 * mutually exclusive with the command palette).
 *
 * Safe areas: being `fixed inset-0`, the overlay covers the status bar and the
 * home indicator, so its chrome — close button, counter — has to inset itself
 * or it lands underneath them. The `max(env(...), …)` floors below handle
 * mobile Safari. They are NOT enough in the Capacitor shell: WKWebView reports
 * env(safe-area-inset-top) as 0 while still rendering edge-to-edge, so the
 * floor collapses and the close button sits under the dynamic island. A
 * `.native-shell` rule in index.css forces a real top inset there, keyed on
 * the data-testids below — the same pattern the onboarding top bar and the
 * toast container already use.
 */
export function Lightbox({ images, index, onIndexChange, onClose, toUrl }: LightboxProps) {
  const { t: tc } = useTranslation('common')
  const resolve = toUrl ?? ((raw: string) => raw)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight' && index < images.length - 1) onIndexChange(index + 1)
      else if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1)
    }
    document.addEventListener('keydown', onKey)
    // Locking body overflow while the page is scrolled collapses the scroll and
    // drops the viewport to the top; restoring overflow afterwards does not put
    // it back. On a long list — bank reconciliation, parcels — opening a
    // thumbnail therefore lost the operator's place entirely, which reads as the
    // page having navigated somewhere. Remember the offset and restore it.
    const prevOverflow = document.body.style.overflow
    const scrollY = window.scrollY
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      window.scrollTo({ top: scrollY, behavior: 'instant' as ScrollBehavior })
    }
  }, [index, images.length, onIndexChange, onClose])

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 sm:p-10 lg:p-16"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose() }}
        data-testid="lightbox-close"
        className="absolute right-4 top-[max(env(safe-area-inset-top),1rem)] text-white/80 hover:text-white p-2"
        aria-label={tc('labels.close')}
      >
        <X className="w-6 h-6" />
      </button>
      <ImageWithFallback
        src={resolve(images[index])}
        alt=""
        showLabel
        iconClassName="w-12 h-12"
        className="max-w-full max-h-[80vh] min-w-40 min-h-40 rounded-lg object-contain bg-base-100/5"
        onClick={(e) => e.stopPropagation()}
      />
      {images.length > 1 && (
        <>
          <div
            data-testid="lightbox-counter"
            className="absolute bottom-[max(env(safe-area-inset-bottom),1rem)] left-1/2 -translate-x-1/2 text-white/80 text-sm"
          >
            {index + 1} / {images.length}
          </div>
          {index > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onIndexChange(index - 1) }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 text-3xl"
              aria-label={tc('pagination.previous')}
            >
              ‹
            </button>
          )}
          {index < images.length - 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onIndexChange(index + 1) }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 text-3xl"
              aria-label={tc('pagination.next')}
            >
              ›
            </button>
          )}
        </>
      )}
    </div>,
    document.body,
  )
}
